/**
 * `apiFetch` — the one call every page should use to reach either backend.
 *
 * Why a module-level session handle rather than a hook: components copied
 * across from `src/components/` call bare `fetch("/api/…")` inside TanStack
 * Query `queryFn`s, far from any React context. Porting them (FE-03: reuse,
 * don't rewrite) should be a rename, not a refactor into a hook — so the
 * `AuthSession` the `AuthProvider` builds is registered here once and
 * `fetch(` becomes `apiFetch(`.
 *
 * Before the session registers (or after sign-out) this degrades to a plain
 * same-origin `fetch` WITH cookies. That is not a hole: it is exactly what the
 * un-ported Next.js routes authenticate with anyway, and a FastAPI route
 * reached without a bearer answers 401 as it should.
 */
import { AuthError } from "../auth/types";
import type { AuthSession } from "../auth/session";

let current: AuthSession | null = null;

/** Called by `AuthProvider`. Passing `null` unregisters (tests, teardown). */
export function setApiSession(session: AuthSession | null): void {
  current = session;
}

export function getApiSession(): AuthSession | null {
  return current;
}

/**
 * Bearer + cookies, with one 401 healed by a token rotation.
 *
 * `no_session` is translated into a real 401 `Response` rather than a thrown
 * `AuthError`, so a `queryFn` written against `fetch` semantics ("check
 * `res.ok`") keeps behaving the way it was written.
 */
/**
 * Wave-3: the server-side forced-password-change gate answers 403
 * `password_change_required` on every data route. A session issued BEFORE the
 * flag existed carries no `mustChangePassword`, so the client cannot know from
 * its stored user alone — it finds out HERE, from the first gated response,
 * and the ForcePasswordChangeGate subscribes. Without this, a pre-migration
 * session renders error cards on every page instead of the gate.
 */
type GateListener = () => void;
const gateListeners = new Set<GateListener>();
let gateTripped = false;
export function passwordGateTripped(): boolean {
  return gateTripped;
}
export function onPasswordGate(listener: GateListener): () => void {
  gateListeners.add(listener);
  return () => gateListeners.delete(listener);
}
export function resetPasswordGate(): void {
  gateTripped = false;
  for (const l of gateListeners) l();
}
async function detectPasswordGate(res: Response): Promise<Response> {
  if (res.status === 403 && !gateTripped) {
    try {
      const peek = await res.clone().json();
      if (peek?.detail === "password_change_required") {
        gateTripped = true;
        for (const l of gateListeners) l();
      }
    } catch {
      /* not JSON — not the gate */
    }
  }
  return res;
}

export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  if (!current) {
    return fetch(input, { credentials: "include", ...init }).then(detectPasswordGate);
  }
  try {
    return await current.authorizedFetch(input, init).then(detectPasswordGate);
  } catch (err) {
    if (err instanceof AuthError && err.code === "no_session") {
      return new Response(JSON.stringify({ error: "Unauthorized" }), {
        status: 401,
        headers: { "content-type": "application/json" },
      });
    }
    throw err;
  }
}

/** `apiFetch` + `res.ok` check + JSON parse — the shape most `queryFn`s want. */
export async function apiJson<T>(input: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(input, init);
  if (!res.ok) {
    throw new Error(`Request failed (${res.status}) — ${input}`);
  }
  return (await res.json()) as T;
}
